import React, { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components";

import Dropdown from "components/atoms/Dropdown";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";

const ToolbarContainer = styled.div`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 44px;
    padding: 0 16px;
    border-bottom: solid 1px var(--color-Line);
    background-color: var(--color-White);
`;

const ToolbarFrame = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: min(100%, 860px);
    min-width: 0;
`;

const ToolbarViewport = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    min-width: 0;
    overflow: hidden;
`;

const ToolbarInline = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
`;

const ToolbarActions = styled.div`
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const ToolbarGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;

    &.overflow-hidden {
        display: none;
    }
`;

const Divider = styled.div`
    width: 1px;
    height: 18px;
    margin: 0 2px;
    background: var(--color-Line);
`;

const ToolButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid ${(props) => (props.$active ? "var(--color-Key)" : "transparent")};
    border-radius: 4px;
    background: ${(props) => (props.$active ? "var(--color-Button4)" : "transparent")};
    color: var(--color-SubBlack);
    font-size: 1.2rem;
    font-weight: 700;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: var(--color-Base1);
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const BlockSelect = styled.select`
    height: 28px;
    min-width: 92px;
    padding: 0 24px 0 8px;
    border: 1px solid var(--color-Line);
    border-radius: 4px;
    background: var(--color-White);
    color: var(--color-SubBlack);
    font-size: 1.2rem;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const OverflowAnchor = styled.div`
    display: ${(props) => (props.$visible ? "inline-flex" : "none")};
`;

const OverflowMenu = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    background: var(--color-White);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
`;

const MenuPanel = styled.div`
    min-width: 180px;
    padding: 8px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    background: var(--color-White);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
`;

const MenuItem = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 30px;
    padding: 0 8px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: var(--color-SubBlack);
    font-size: 1.2rem;
    text-align: left;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: var(--color-Base1);
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const PaletteGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 24px);
    gap: 6px;
    padding: 8px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    background: var(--color-White);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
`;

const SwatchButton = styled.button`
    width: 24px;
    height: 24px;
    border: 1px solid var(--color-Line);
    border-radius: 4px;
    background: ${(props) => props.$color};
    cursor: pointer;
`;

const DEFAULT_COLOR = "#242424";
const DEFAULT_BACKGROUND = "transparent";
const PALETTE = ["#242424", "#6b7280", "#dc2626", "#d97706", "#0f8f6f", "#2563eb", "#7c3aed", "#ffffff"];
const BACKGROUND_PALETTE = ["transparent", "#f3f4f6", "#fee2e2", "#fef3c7", "#dcfce7", "#dbeafe", "#ede9fe", "#fff7ed"];

function getBlockValue(editor) {
    if (!editor) return "paragraph";
    if (editor.isActive("heading", { level: 1 })) return "heading-1";
    if (editor.isActive("heading", { level: 2 })) return "heading-2";
    if (editor.isActive("heading", { level: 3 })) return "heading-3";
    return "paragraph";
}

function runCommand(editor, command) {
    if (!editor) return;
    command(editor.chain().focus()).run();
}

function canRunCommand(editor, commandName) {
    if (!editor || editor.isDestroyed) return false;
    try {
        const can = editor.can();
        return Boolean(can?.[commandName]?.());
    } catch (_error) {
        return false;
    }
}

function ToolbarIconButton({ active, children, disabled, icon, label, onClick }) {
    return (
        <Tooltip message={label}>
            <ToolButton type="button" aria-label={label} $active={active} disabled={disabled} onClick={onClick}>
                {icon ? <Icon name={icon} size="13px" color="var(--color-SubBlack)" /> : children}
            </ToolButton>
        </Tooltip>
    );
}

function PaletteDropdown({ disabled, dropdownUsePortal = true, icon, label, onSelect, palette }) {
    const [open, setOpen] = useState(false);
    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
            placement="bottom-start"
            offset={6}
            usePortal={dropdownUsePortal}
            anchor={
                <ToolbarIconButton
                    disabled={disabled}
                    icon={icon}
                    label={label}
                    onClick={() => setOpen((current) => !current)}
                />
            }
        >
            {open ? (
                <PaletteGrid aria-label={label}>
                    {palette.map((color) => (
                        <SwatchButton
                            key={color}
                            type="button"
                            aria-label={color === "transparent" ? "색상 없음" : color}
                            $color={color}
                            onClick={() => {
                                onSelect(color);
                                setOpen(false);
                            }}
                        />
                    ))}
                </PaletteGrid>
            ) : null}
        </Dropdown>
    );
}

function TableDropdown({ disabled, dropdownUsePortal = true, editor }) {
    const [open, setOpen] = useState(false);
    const runTableCommand = (callback) => {
        if (!editor) return;
        callback(editor.chain().focus()).run();
        setOpen(false);
    };
    const insertTableAfterCurrentBlock = () => {
        if (!editor) return;
        const { $from } = editor.state.selection;
        const insertPosition = $from.depth > 0 ? $from.after(1) : editor.state.doc.content.size;
        const inserted = editor
            .chain()
            .focus()
            .setTextSelection(insertPosition)
            .insertTable({ rows: 3, cols: 3, withHeaderRow: false })
            .run();
        if (!inserted) {
            editor.chain().focus("end").insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run();
        }
        setOpen(false);
    };
    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
            placement="bottom-start"
            offset={6}
            contentMinWidth="180px"
            usePortal={dropdownUsePortal}
            anchor={
                <ToolbarIconButton
                    disabled={disabled}
                    icon="table"
                    label="표"
                    onClick={() => setOpen((current) => !current)}
                />
            }
        >
            {open ? (
                <MenuPanel role="menu" aria-label="표">
                    <MenuItem type="button" onClick={insertTableAfterCurrentBlock}>
                        3x3 표 삽입
                    </MenuItem>
                    <MenuItem
                        type="button"
                        disabled={!editor?.isActive("table")}
                        onClick={() => runTableCommand((chain) => chain.addRowAfter())}
                    >
                        아래 행 추가
                    </MenuItem>
                    <MenuItem
                        type="button"
                        disabled={!editor?.isActive("table")}
                        onClick={() => runTableCommand((chain) => chain.addColumnAfter())}
                    >
                        오른쪽 열 추가
                    </MenuItem>
                    <MenuItem
                        type="button"
                        disabled={!editor?.isActive("table")}
                        onClick={() => runTableCommand((chain) => chain.deleteRow())}
                    >
                        행 삭제
                    </MenuItem>
                    <MenuItem
                        type="button"
                        disabled={!editor?.isActive("table")}
                        onClick={() => runTableCommand((chain) => chain.deleteColumn())}
                    >
                        열 삭제
                    </MenuItem>
                    <MenuItem
                        type="button"
                        disabled={!editor?.isActive("table")}
                        onClick={() => runTableCommand((chain) => chain.deleteTable())}
                    >
                        표 삭제
                    </MenuItem>
                </MenuPanel>
            ) : null}
        </Dropdown>
    );
}

const ALIGNMENT_OPTIONS = [
    { id: "left", icon: "alignLeft", label: "왼쪽 정렬" },
    { id: "center", icon: "alignCenter", label: "가운데 정렬" },
    { id: "right", icon: "alignRight", label: "오른쪽 정렬" },
    { id: "justify", icon: "alignJustify", label: "양쪽 정렬" },
];

function getActiveAlignment(editor) {
    return ALIGNMENT_OPTIONS.find((option) => editor?.isActive({ textAlign: option.id })) || ALIGNMENT_OPTIONS[0];
}

function AlignmentDropdown({ disabled, dropdownUsePortal = true, editor }) {
    const [open, setOpen] = useState(false);
    const activeAlignment = getActiveAlignment(editor);

    const setAlignment = (alignment) => {
        runCommand(editor, (chain) => chain.setTextAlign(alignment));
        setOpen(false);
    };

    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
            placement="bottom-start"
            offset={6}
            usePortal={dropdownUsePortal}
            anchor={
                <ToolbarIconButton
                    active={ALIGNMENT_OPTIONS.some((option) => editor?.isActive({ textAlign: option.id }))}
                    disabled={disabled}
                    icon={activeAlignment.icon}
                    label="문단 정렬"
                    onClick={() => setOpen((current) => !current)}
                />
            }
        >
            {open ? (
                <MenuPanel role="menu" aria-label="문단 정렬">
                    {ALIGNMENT_OPTIONS.map((option) => (
                        <MenuItem key={option.id} type="button" onClick={() => setAlignment(option.id)}>
                            <Icon
                                name={option.icon}
                                size="14px"
                                color={
                                    editor?.isActive({ textAlign: option.id })
                                        ? "var(--color-Key)"
                                        : "var(--color-SubBlack)"
                                }
                            />
                            {option.label}
                        </MenuItem>
                    ))}
                </MenuPanel>
            ) : null}
        </Dropdown>
    );
}

function NoteEditorToolbar({ disabled, editor, isSaving, onPrint, onSaveRevision }) {
    const viewportRef = useRef(null);
    const inlineRef = useRef(null);
    const frameRef = useRef(null);
    const [overflowGroups, setOverflowGroups] = useState(new Set());
    const [overflowOpen, setOverflowOpen] = useState(false);
    const [, setRenderTick] = useState(0);
    const commandDisabled = disabled || !editor;

    const buildOverflow = useCallback(() => {
        const viewport = viewportRef.current;
        const inline = inlineRef.current;
        if (!viewport || !inline) return;

        const groups = Array.from(inline.querySelectorAll("[data-overflow]")).reverse();
        groups.forEach((group) => group.classList.remove("overflow-hidden"));

        const nextOverflow = [];
        const availableWidth = Math.max(0, viewport.clientWidth - 46);
        for (const group of groups) {
            if (inline.scrollWidth <= availableWidth) break;
            nextOverflow.push(group.dataset.overflow);
            group.classList.add("overflow-hidden");
        }

        const normalized = new Set(nextOverflow.filter(Boolean));
        setOverflowGroups((current) => {
            const sameSize = current.size === normalized.size;
            const sameValues = sameSize && Array.from(current).every((value) => normalized.has(value));
            return sameValues ? current : normalized;
        });
        if (!normalized.size) setOverflowOpen(false);
    }, []);

    const scheduleOverflow = useCallback(() => {
        if (frameRef.current !== null) {
            window.cancelAnimationFrame(frameRef.current);
        }
        frameRef.current = window.requestAnimationFrame(() => {
            frameRef.current = null;
            buildOverflow();
        });
    }, [buildOverflow]);

    useEffect(() => {
        if (!editor) return undefined;
        const refresh = () => {
            setRenderTick((tick) => tick + 1);
        };
        editor.on("selectionUpdate", refresh);
        refresh();
        return () => {
            editor.off("selectionUpdate", refresh);
        };
    }, [editor]);

    useEffect(() => {
        const viewport = viewportRef.current;
        const inline = inlineRef.current;
        if (!viewport || !inline) return undefined;

        let observer;
        if ("ResizeObserver" in window) {
            observer = new ResizeObserver(scheduleOverflow);
            observer.observe(viewport);
            observer.observe(inline);
        } else {
            window.addEventListener("resize", scheduleOverflow);
        }
        scheduleOverflow();

        return () => {
            observer?.disconnect();
            window.removeEventListener("resize", scheduleOverflow);
        };
    }, [scheduleOverflow]);

    useEffect(() => {
        return () => {
            if (frameRef.current !== null) {
                window.cancelAnimationFrame(frameRef.current);
            }
        };
    }, []);

    const handleBlockChange = (event) => {
        const value = event.target.value;
        if (value === "paragraph") {
            runCommand(editor, (chain) => chain.setParagraph());
            return;
        }
        const level = Number(value.replace("heading-", ""));
        runCommand(editor, (chain) => chain.toggleHeading({ level }));
    };

    const handleLink = () => {
        if (!editor) return;
        const previousUrl = editor.getAttributes("link").href || "";
        const url = window.prompt("링크 URL", previousUrl);
        if (url === null) return;
        if (!url.trim()) {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
    };

    const insertMediaByUrl = (type) => {
        if (!editor) return;
        const label = type === "noteImage" ? "이미지 URL" : "비디오 URL";
        const url = window.prompt(label);
        if (!url?.trim()) return;
        editor
            .chain()
            .focus()
            .insertContent({ type, attrs: { src: url.trim(), provider: type === "noteVideo" ? "url" : undefined } })
            .run();
    };

    const toolbarContent = (overflowed = null) => {
        const isOverflowMenu = Boolean(overflowed);
        const canShow = (group) => (isOverflowMenu ? overflowed.has(group) : true);
        return (
            <>
                {canShow("group-history") && (
                    <ToolbarGroup data-overflow="group-history" aria-label="실행">
                        <ToolbarIconButton
                            disabled={commandDisabled || !canRunCommand(editor, "undo")}
                            icon="undo"
                            label="실행취소"
                            onClick={() => runCommand(editor, (chain) => chain.undo())}
                        />
                        <ToolbarIconButton
                            disabled={commandDisabled || !canRunCommand(editor, "redo")}
                            icon="redo"
                            label="다시실행"
                            onClick={() => runCommand(editor, (chain) => chain.redo())}
                        />
                        <ToolbarIconButton disabled={disabled} icon="print" label="인쇄" onClick={onPrint} />
                    </ToolbarGroup>
                )}
                {canShow("group-block") && (
                    <ToolbarGroup data-overflow="group-block" aria-label="블록">
                        <Divider />
                        <BlockSelect
                            aria-label="문단 형식"
                            value={getBlockValue(editor)}
                            disabled={commandDisabled}
                            onChange={handleBlockChange}
                        >
                            <option value="paragraph">본문</option>
                            <option value="heading-1">제목 1</option>
                            <option value="heading-2">제목 2</option>
                            <option value="heading-3">제목 3</option>
                        </BlockSelect>
                    </ToolbarGroup>
                )}
                {canShow("group-format") && (
                    <ToolbarGroup data-overflow="group-format" aria-label="텍스트 서식">
                        <Divider />
                        <ToolbarIconButton
                            active={editor?.isActive("bold")}
                            disabled={commandDisabled}
                            icon="textBold"
                            label="굵게"
                            onClick={() => runCommand(editor, (chain) => chain.toggleBold())}
                        />
                        <ToolbarIconButton
                            active={editor?.isActive("italic")}
                            disabled={commandDisabled}
                            icon="textItalic"
                            label="기울임"
                            onClick={() => runCommand(editor, (chain) => chain.toggleItalic())}
                        />
                        <ToolbarIconButton
                            active={editor?.isActive("underline")}
                            disabled={commandDisabled}
                            icon="textUnderline"
                            label="밑줄"
                            onClick={() => runCommand(editor, (chain) => chain.toggleUnderline())}
                        />
                        <ToolbarIconButton
                            active={editor?.isActive("strike")}
                            disabled={commandDisabled}
                            icon="textStrike"
                            label="취소선"
                            onClick={() => runCommand(editor, (chain) => chain.toggleStrike())}
                        />
                        <PaletteDropdown
                            disabled={commandDisabled}
                            dropdownUsePortal={!isOverflowMenu}
                            icon="styleTextColor"
                            label="글자색"
                            palette={PALETTE}
                            onSelect={(color) =>
                                runCommand(editor, (chain) =>
                                    color === DEFAULT_COLOR ? chain.unsetStyledText() : chain.setStyledText({ color }),
                                )
                            }
                        />
                        <PaletteDropdown
                            disabled={commandDisabled}
                            dropdownUsePortal={!isOverflowMenu}
                            icon="styleTextBgColor"
                            label="배경색"
                            palette={BACKGROUND_PALETTE}
                            onSelect={(backgroundColor) =>
                                runCommand(editor, (chain) =>
                                    backgroundColor === DEFAULT_BACKGROUND
                                        ? chain.unsetStyledText()
                                        : chain.setStyledText({ backgroundColor }),
                                )
                            }
                        />
                    </ToolbarGroup>
                )}
                {canShow("group-align") && (
                    <ToolbarGroup data-overflow="group-align" aria-label="정렬 및 목록">
                        <Divider />
                        <AlignmentDropdown
                            disabled={commandDisabled}
                            dropdownUsePortal={!isOverflowMenu}
                            editor={editor}
                        />
                        <ToolbarIconButton
                            active={editor?.isActive("bulletList")}
                            disabled={commandDisabled}
                            icon="list"
                            label="글머리 목록"
                            onClick={() => runCommand(editor, (chain) => chain.toggleBulletList())}
                        />
                        <ToolbarIconButton
                            active={editor?.isActive("orderedList")}
                            disabled={commandDisabled}
                            icon="listNum"
                            label="번호 목록"
                            onClick={() => runCommand(editor, (chain) => chain.toggleOrderedList())}
                        />
                    </ToolbarGroup>
                )}
                {canShow("group-structure") && (
                    <ToolbarGroup data-overflow="group-structure" aria-label="구조">
                        <Divider />
                        <ToolbarIconButton
                            active={editor?.isActive("blockquote")}
                            disabled={commandDisabled}
                            icon="quote"
                            label="인용구"
                            onClick={() => runCommand(editor, (chain) => chain.toggleBlockquote())}
                        />
                        <ToolbarIconButton
                            active={editor?.isActive("code")}
                            disabled={commandDisabled}
                            label="인라인 코드"
                            onClick={() => runCommand(editor, (chain) => chain.toggleCode())}
                        >
                            {"</>"}
                        </ToolbarIconButton>
                        <ToolbarIconButton
                            disabled={commandDisabled}
                            icon="minus"
                            label="구분선"
                            onClick={() => runCommand(editor, (chain) => chain.setHorizontalRule())}
                        />
                    </ToolbarGroup>
                )}
                {canShow("group-insert") && (
                    <ToolbarGroup data-overflow="group-insert" aria-label="삽입">
                        <Divider />
                        <ToolbarIconButton
                            active={editor?.isActive("link")}
                            disabled={commandDisabled}
                            icon="link"
                            label="링크"
                            onClick={handleLink}
                        />
                        <ToolbarIconButton
                            disabled={commandDisabled}
                            icon="imageBlock"
                            label="이미지"
                            onClick={() => insertMediaByUrl("noteImage")}
                        />
                        <ToolbarIconButton
                            disabled={commandDisabled}
                            icon="play"
                            label="비디오"
                            onClick={() => insertMediaByUrl("noteVideo")}
                        />
                        <TableDropdown disabled={commandDisabled} dropdownUsePortal={!isOverflowMenu} editor={editor} />
                    </ToolbarGroup>
                )}
            </>
        );
    };

    return (
        <ToolbarContainer>
            <ToolbarFrame>
                <ToolbarViewport ref={viewportRef}>
                    <ToolbarInline ref={inlineRef}>
                        <ToolbarIconButton
                            disabled={disabled || isSaving}
                            icon="save"
                            label="현재 상태 저장"
                            onClick={onSaveRevision}
                        />
                        {toolbarContent()}
                    </ToolbarInline>
                </ToolbarViewport>
                <ToolbarActions>
                    <OverflowAnchor $visible={overflowGroups.size > 0}>
                        <Dropdown
                            open={overflowOpen}
                            onOpenChange={setOverflowOpen}
                            placement="bottom-end"
                            offset={6}
                            contentMinWidth="300px"
                            anchor={
                                <ToolbarIconButton
                                    icon="submenu"
                                    label="더보기"
                                    onClick={() => setOverflowOpen((current) => !current)}
                                />
                            }
                        >
                            {overflowOpen && overflowGroups.size > 0 ? (
                                <OverflowMenu role="group" aria-label="더보기 도구 모음">
                                    {toolbarContent(overflowGroups)}
                                </OverflowMenu>
                            ) : null}
                        </Dropdown>
                    </OverflowAnchor>
                </ToolbarActions>
            </ToolbarFrame>
        </ToolbarContainer>
    );
}

export default React.memo(NoteEditorToolbar);
